/**
 * Alpine components for the moderator support inbox thread view (issue #3275).
 * Every mutation posts JSON to the /v1/mod-support endpoints via
 * window.ApiClient. Thread ids are strings throughout (they exceed
 * Number.MAX_SAFE_INTEGER). Confirmation goes through window.ConfirmModal;
 * toasts through window.showSuccess / window.showError.
 */

function apiPostJson(path, payload) {
    return window.ApiClient.request(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
    });
}

function confirmAction(options) {
    return new Promise((resolve) => {
        if (!window.ConfirmModal || typeof window.ConfirmModal.show !== 'function') {
            resolve(window.confirm(options.message));
            return;
        }
        window.ConfirmModal.show({
            title: options.title,
            message: options.message,
            confirmText: options.confirmText,
            cancelText: 'Cancel',
            type: options.type || 'warning',
            onConfirm: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

/**
 * Show a toast, then navigate after a beat so the toast is actually seen —
 * a synchronous reload tears the page down before the toast renders.
 */
function toastThenGo(toast, url) {
    toast();
    setTimeout(() => {
        if (url) {
            window.location.href = url;
        } else {
            window.location.reload();
        }
    }, 1200);
}

async function errorMessage(response, fallback) {
    let message = fallback || ('Request failed (' + response.status + ').');
    try {
        const body = await response.json();
        if (body && body.error && body.error.message) {
            message = body.error.message;
        }
    } catch (_e) {
        // Body wasn't JSON; keep the fallback.
    }
    return message;
}

/**
 * Thread header actions: escalate/de-escalate, snooze, resolve, and merge.
 * A successful mutation reloads the page so the server-rendered thread reflects
 * the new state (status pill, timeline, escalation banner).
 */
export function modSupportThread(config = {}) {
    return {
        threadId: String(config.threadId || ''),
        status: config.status || 'open',
        supportUrl: config.supportUrl || '',
        busy: false,
        // Latched before a toast-then-navigate: keeps the action buttons
        // disabled through the delay so nothing can double-submit while the
        // old page is still visible.
        leaving: false,
        snoozeOpen: false,
        menuOpen: false,
        mergeOpen: false,
        mergeTargetId: '',
        mergeError: '',

        async setEscalation(escalated) {
            if (this.busy) return;
            this.busy = true;
            try {
                const response = await apiPostJson(
                    `/mod-support/threads/${this.threadId}/escalate`,
                    { escalated },
                );
                if (response.ok) {
                    // Reload in place (the mod keeps working the thread), with
                    // the buttons latched so nothing double-submits while the
                    // old page is still visible.
                    this.leaving = true;
                    window.location.reload();
                    return;
                }
                window.showError?.(await errorMessage(response, 'Could not update escalation.'));
            } catch (_e) {
                window.showError?.('Network error. Please try again.');
            } finally {
                if (!this.leaving) this.busy = false;
            }
        },

        async snooze(days) {
            this.snoozeOpen = false;
            if (this.busy) return;
            this.busy = true;
            const until = new Date(Date.now() + days * 86400000).toISOString();
            try {
                // Keep the current status; snoozing only sets the wake time.
                const response = await apiPostJson(
                    `/mod-support/threads/${this.threadId}/status`,
                    { status: this.status, snoozed_until: until },
                );
                if (response.ok) {
                    this.leaving = true;
                    toastThenGo(() => window.showSuccess?.('Thread snoozed.'), `${this.supportUrl}?lane=open`);
                    return;
                }
                window.showError?.(await errorMessage(response, 'Could not snooze the thread.'));
            } catch (_e) {
                window.showError?.('Network error. Please try again.');
            } finally {
                if (!this.leaving) this.busy = false;
            }
        },

        async resolve() {
            if (this.busy) return;
            const ok = await confirmAction({
                title: 'Resolve thread',
                message: 'Mark this thread resolved? No email is sent to the user.',
                confirmText: 'Resolve',
                type: 'info',
            });
            if (!ok) return;

            this.busy = true;
            try {
                const response = await apiPostJson(
                    `/mod-support/threads/${this.threadId}/status`,
                    { status: 'resolved' },
                );
                if (response.ok) {
                    // Land back on the queue: resolving is a terminal action and
                    // the next ticket is what the mod wants on screen.
                    this.leaving = true;
                    toastThenGo(() => window.showSuccess?.('Thread resolved.'), `${this.supportUrl}?lane=open`);
                    return;
                }
                window.showError?.(await errorMessage(response, 'Could not resolve the thread.'));
            } catch (_e) {
                window.showError?.('Network error. Please try again.');
            } finally {
                if (!this.leaving) this.busy = false;
            }
        },

        async merge() {
            const target = String(this.mergeTargetId || '').trim();
            this.mergeError = '';
            if (!/^\d+$/.test(target)) {
                this.mergeError = 'Enter a numeric thread ID.';
                return;
            }
            if (target === this.threadId) {
                this.mergeError = 'A thread cannot be merged into itself.';
                return;
            }
            if (this.busy) return;
            this.busy = true;
            try {
                const response = await apiPostJson(
                    `/mod-support/threads/${this.threadId}/merge`,
                    { target_thread_id: target },
                );
                if (response.ok) {
                    this.leaving = true;
                    toastThenGo(() => window.showSuccess?.('Thread merged.'), `${this.supportUrl}/thread/${target}`);
                    return;
                }
                if (response.status === 404) {
                    this.mergeError = 'Target thread not found.';
                    return;
                }
                this.mergeError = await errorMessage(response, 'Could not merge the thread.');
            } catch (_e) {
                this.mergeError = 'Network error. Please try again.';
            } finally {
                if (!this.leaving) this.busy = false;
            }
        },
    };
}

/**
 * Reply composer with an internal-note toggle. A staff reply posts to /reply
 * with the chosen result status; a bounce block (409) surfaces inline with a
 * "Send anyway" retry that repeats the request with force=true. Notes post to
 * /notes and are never sent to the user. A successful send reloads the page.
 */
export function modSupportComposer(config = {}) {
    return {
        threadId: String(config.threadId || ''),
        supportUrl: config.supportUrl || '',
        body: '',
        isNote: false,
        sending: false,
        // Latched before a toast-then-reload: replies run with duplicate
        // checking off server-side, so the disabled composer is the guard
        // against an accidental second identical send during the delay.
        leaving: false,
        force: false,
        bounceBlocked: false,
        error: '',
        lastResultStatus: 'resolved',

        init() {
            try {
                const raw = sessionStorage.getItem(this.draftKey());
                if (raw) {
                    const saved = JSON.parse(raw);
                    this.body = saved.body || '';
                    this.isNote = !!saved.isNote;
                }
            } catch (_e) {
                // Storage unavailable; drafts just don't persist.
            }
            this.$watch('body', () => this.saveDraft());
            this.$watch('isNote', () => this.saveDraft());
        },

        draftKey() {
            return 'modSupportDraft:' + this.threadId;
        },

        saveDraft() {
            try {
                if (this.body.trim() === '') {
                    sessionStorage.removeItem(this.draftKey());
                } else {
                    sessionStorage.setItem(this.draftKey(), JSON.stringify({ body: this.body, isNote: this.isNote }));
                }
            } catch (_e) {
                // Storage unavailable or full; drafts just don't persist.
            }
        },

        clearDraft() {
            try {
                sessionStorage.removeItem(this.draftKey());
            } catch (_e) {
                // Nothing to clear.
            }
        },

        toggleNote() {
            this.isNote = !this.isNote;
            this.error = '';
            this.bounceBlocked = false;
        },

        submitPrimary() {
            if (this.isNote) {
                this.addNote();
            } else {
                this.send('resolved');
            }
        },

        async send(resultStatus) {
            // Consume the force flag before any early return: it applies to
            // exactly one request, and an aborted attempt (empty body, already
            // sending) must not latch a bounce bypass onto a later send.
            const force = this.force;
            this.force = false;

            const text = this.body.trim();
            if (!text || this.sending) return;
            this.lastResultStatus = resultStatus;
            this.error = '';
            this.bounceBlocked = false;
            this.sending = true;
            try {
                const response = await apiPostJson(
                    `/mod-support/threads/${this.threadId}/reply`,
                    { body: this.body, result_status: resultStatus, force },
                );
                if (response.ok) {
                    const result = await response.json().catch(() => null);
                    this.leaving = true;
                    this.clearDraft();
                    if (result && result.bounced === true) {
                        // Persisted but not delivered (the address bounced at
                        // send time). Reload in place so the timeline shows the
                        // message with its Bounced chip and the event note.
                        toastThenGo(() => window.showError?.('Reply was not delivered. The address bounced.'));
                    } else {
                        // Land back on the queue: the reply is out and the next
                        // ticket is what the mod wants on screen.
                        toastThenGo(() => window.showSuccess?.('Reply sent.'), `${this.supportUrl}?lane=open`);
                    }
                    return;
                }
                if (response.status === 409) {
                    const body = await response.json().catch(() => null);
                    const code = body && body.error ? body.error.code : null;
                    const message = body && body.error && body.error.message
                        ? body.error.message
                        : 'Could not send the reply.';
                    // Only the bounce block is retryable with force; a merged
                    // thread 409 would loop forever on "Send anyway".
                    this.bounceBlocked = code === 'support_reply_bounce_blocked';
                    this.error = message;
                    return;
                }
                this.bounceBlocked = false;
                this.error = await errorMessage(response, 'Could not send the reply.');
            } catch (_e) {
                this.error = 'Network error. Please try again.';
            } finally {
                if (!this.leaving) this.sending = false;
            }
        },

        sendAnyway() {
            // Only meaningful straight after a bounce-block 409; a click from
            // any other state must not flip force on a fresh send.
            if (!this.error) return;
            this.force = true;
            this.send(this.lastResultStatus);
        },

        async addNote() {
            const text = this.body.trim();
            if (!text || this.sending) return;
            this.error = '';
            this.sending = true;
            try {
                const response = await apiPostJson(
                    `/mod-support/threads/${this.threadId}/notes`,
                    { body: this.body },
                );
                if (response.ok) {
                    this.leaving = true;
                    this.clearDraft();
                    toastThenGo(() => window.showSuccess?.('Note added.'));
                    return;
                }
                this.error = await errorMessage(response, 'Could not add the note.');
            } catch (_e) {
                this.error = 'Network error. Please try again.';
            } finally {
                if (!this.leaving) this.sending = false;
            }
        },
    };
}
